import React, { useState, useEffect, useRef, useCallback } from 'react';
import { CalendarMonth, AttachMoney, Description } from '@mui/icons-material';
import { Modal } from '@components/molecules/Modal';
import { Input } from '@components/atoms/Input';
import { Card } from '@components/atoms/Card';
import ReactSelect from '@components/atoms/ReactSelect';
import { FileDropZone } from '@components/molecules/FileDropZone';
import { BudgetRequestCreateInput, BudgetRequestStatus } from '@models/budget.model';
import { Task, TaskStatus } from '@models/task.model';
import { Organization } from '@models/organization.model';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { graphQlClient } from '@config/graphqlClient';
import TasksGraphQL from '@graphql/task.queries';
import OrganizationsGraphQL from '@graphql/organization.queries';
import { useProjects } from '@hooks/api/projects';
import { useBudget } from '@hooks/api/useBudget';
import { parseTaskDate, formatTaskDateForBackend } from '@utils/dateOnlyParser';
import { htmlToPlainText } from '@utils/richText';

interface CreateBudgetModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: BudgetRequestCreateInput) => void;
  isSubmitting: boolean;
}

const validationSchema = Yup.object({
  projectId: Yup.string().required('Project is required'),
  taskId: Yup.string().required('Task is required'),
  requestedStartDate: Yup.date()
    .required('Start date is required')
    .typeError('Invalid date'),
  requestedEndDate: Yup.date()
    .required('End date is required')
    .min(Yup.ref('requestedStartDate'), 'End date must be after start date')
    .typeError('Invalid date'),
  maxBudgetAmount: Yup.number()
    .positive('Amount must be positive')
    .typeError('Invalid amount'),
  description: Yup.string(),
  recipientOrganizationIds: Yup.array()
    .of(Yup.string())
    .min(1, 'At least one recipient organization is required'),
});

const CreateBudgetModal: React.FC<CreateBudgetModalProps> = ({
  open,
  onClose,
  onSubmit,
  isSubmitting,
}) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [loadingOrganizations, setLoadingOrganizations] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [tasksWithActiveBudgets, setTasksWithActiveBudgets] = useState<
    Set<string>
  >(new Set());
  const documentsListRef = useRef<HTMLDivElement>(null);

  // Use the existing useProjects hook instead of manual fetch
  const { data: projects = [], isLoading: loadingProjects } = useProjects();
  const { fetchBudgetRequestsByTaskId } = useBudget();

  const formik = useFormik({
    initialValues: {
      projectId: '',
      taskId: '',
      requestedStartDate: '',
      requestedEndDate: '',
      maxBudgetAmount: '',
      description: '',
      recipientOrganizationIds: [] as string[],
    },
    validationSchema,
    onSubmit: async (values) => {
      const input: BudgetRequestCreateInput = {
        taskId: values.taskId,
        requestedStartDate: values.requestedStartDate,
        requestedEndDate: values.requestedEndDate,
        maxBudgetAmount: values.maxBudgetAmount
          ? parseFloat(values.maxBudgetAmount)
          : undefined,
        description: values.description || undefined,
        recipientOrganizationIds: values.recipientOrganizationIds,
        files: selectedFiles.length > 0 ? selectedFiles : undefined,
      };

      if (selectedFiles.length > 0) {
        setIsUploading(true);
      }

      try {
        await onSubmit(input);
        // Reset form and close modal on success
        formik.resetForm();
        setSelectedFiles([]);
        onClose();
      } catch (error) {
        console.error('Error creating budget request:', error);
      } finally {
        setIsUploading(false);
      }
    },
  });

  // Extract stable references from formik
  const { resetForm, setFieldValue } = formik;
  const projectId = formik.values.projectId;
  const taskId = formik.values.taskId;

  // Wrap fetchTasks in useCallback
  const fetchTasks = useCallback(async (projId: string) => {
    setLoadingTasks(true);
    try {
      const data: any = await graphQlClient.request(
        TasksGraphQL.queries.getTasks,
        { projectId: projId },
      );

      // Filter out tasks that are already delegated or have specific statuses
      const candidateTasks = (data.getTasks || []).filter(
        (task: Task) =>
          !task.delegateOrganizationId && task.status !== TaskStatus.COMPLETE,
      );

      // Check for active budget requests on remaining tasks
      const tasksWithActiveBudgetsSet = new Set<string>();
      const budgetCheckPromises = candidateTasks.map(async (task: Task) => {
        if (task.taskId) {
          try {
            const budgetRequests = await fetchBudgetRequestsByTaskId(
              task.taskId,
            );
            const hasActiveBudget = budgetRequests.some(
              (request: any) => request.status === BudgetRequestStatus.ACTIVE,
            );
            if (hasActiveBudget) {
              tasksWithActiveBudgetsSet.add(task.taskId);
            }
          } catch (error) {
            console.error(
              'Error checking budget requests for task:',
              task.taskId,
              error,
            );
          }
        }
      });

      await Promise.all(budgetCheckPromises);
      setTasksWithActiveBudgets(tasksWithActiveBudgetsSet);

      // Set all tasks (we'll show warning in UI for tasks with active budgets)
      setTasks(candidateTasks);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoadingTasks(false);
    }
  }, [fetchBudgetRequestsByTaskId]);

  // Wrap fetchOrganizations in useCallback
  const fetchOrganizations = useCallback(async (selectedTaskId: string, tasksList: Task[]) => {
    setLoadingOrganizations(true);
    try {
      const data: any = await graphQlClient.request(
        OrganizationsGraphQL.queries.getOrganizationsForTaskCreation,
        {
          organizations: {
            page: 1,
            limit: 100,
          },
        },
      );

      // Get the selected task to exclude its originalOrganizationId
      const selectedTask = tasksList.find((t) => t.taskId === selectedTaskId);

      // Filter for subcontractors and exclude originalOrganization
      const subcontractors = (
        data.getOrganizationsForTaskCreation?.organizations || []
      ).filter(
        (org: Organization) =>
          org.showAsSubcontractor &&
          org.organizationId !== selectedTask?.originalOrganizationId,
      );
      setOrganizations(subcontractors);
    } catch (error) {
      console.error('Error fetching organizations:', error);
    } finally {
      setLoadingOrganizations(false);
    }
  }, []);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      resetForm();
      setSelectedFiles([]);
      setTasksWithActiveBudgets(new Set());
    }
    // Note: Projects are now automatically fetched by useProjects hook
  }, [open, resetForm]);

  // Fetch tasks when project is selected
  useEffect(() => {
    if (projectId) {
      fetchTasks(projectId);
      setFieldValue('taskId', '');
    }
  }, [projectId, fetchTasks, setFieldValue]);

  // Fetch organizations when task is selected
  useEffect(() => {
    if (taskId && tasks.length > 0) {
      fetchOrganizations(taskId, tasks);
    }
  }, [taskId, tasks, fetchOrganizations]);

  // Pre-populate dates when task is selected
  useEffect(() => {
    if (taskId && tasks.length > 0) {
      const selectedTask = tasks.find((t) => t.taskId === taskId);
      if (selectedTask && selectedTask.startTime && selectedTask.endTime) {
        const startDate = parseTaskDate(selectedTask.startTime);
        const endDate = parseTaskDate(selectedTask.endTime);

        // Only set dates if they're not already set and parsing was successful
        if (!formik.values.requestedStartDate && startDate) {
          setFieldValue(
            'requestedStartDate',
            formatTaskDateForBackend(startDate) || '',
          );
        }
        if (!formik.values.requestedEndDate && endDate) {
          setFieldValue(
            'requestedEndDate',
            formatTaskDateForBackend(endDate) || '',
          );
        }
      }

      // Pre-populate budget if task has one and field is empty
      if (
        selectedTask &&
        selectedTask.budget &&
        !formik.values.maxBudgetAmount
      ) {
        setFieldValue('maxBudgetAmount', selectedTask.budget.toString());
      }
    }
  }, [taskId, tasks, setFieldValue, formik.values.requestedStartDate, formik.values.requestedEndDate, formik.values.maxBudgetAmount]);

  // Auto-scroll to document list when files are attached
  useEffect(() => {
    if (selectedFiles.length > 0 && documentsListRef.current) {
      setTimeout(() => {
        documentsListRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }, 100);
    }
  }, [selectedFiles.length]);

  const handleClose = () => {
    if (isUploading) return; // Prevent closing during upload
    formik.resetForm();
    setSelectedFiles([]);
    onClose();
  };

  return (
    <Modal
      isOpen={open}
      onClose={handleClose}
      size="large"
      title="Create Bid Request"
      showCloseButton={true}
      contentClassName="!max-h-[85vh]"
      handleConfirm={() => formik.handleSubmit()}
      confirmText={
        formik.isSubmitting || isSubmitting
          ? 'Creating...'
          : isUploading
            ? 'Uploading...'
            : 'Create Bid Request'
      }
      disableConfirm={
        formik.isSubmitting ||
        isSubmitting ||
        isUploading ||
        !formik.isValid ||
        !formik.dirty ||
        Boolean(
          formik.values.taskId &&
            tasksWithActiveBudgets.has(formik.values.taskId),
        )
      }
      isLoading={formik.isSubmitting || isSubmitting || isUploading}>
      <div className="pt-5">
        <form onSubmit={formik.handleSubmit}>
          <div className="flex flex-col gap-6">
            {/* Project Selection */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                Select Project
              </label>
              <ReactSelect
                options={projects.map((project) => ({
                  value: project.projectId || '',
                  label: project.name || '',
                }))}
                value={
                  formik.values.projectId
                    ? {
                        value: formik.values.projectId,
                        label:
                          projects.find(
                            (p) => p.projectId === formik.values.projectId,
                          )?.name || '',
                      }
                    : null
                }
                onChange={(selectedOption) => {
                  const option = selectedOption as {
                    value: string;
                    label: string;
                  } | null;
                  formik.setFieldValue('projectId', option?.value || '');
                }}
                placeholder="Select a project..."
                isLoading={loadingProjects}
                className="w-full"
                error={
                  formik.touched.projectId && Boolean(formik.errors.projectId)
                }
              />
              {formik.touched.projectId && formik.errors.projectId && (
                <p className="text-red-500 text-sm">
                  {formik.errors.projectId}
                </p>
              )}
            </div>

            {/* Task Selection */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                Select Task
              </label>
              <ReactSelect
                options={tasks.map((task) => ({
                  value: task.taskId || '',
                  label: task.name || '',
                }))}
                value={
                  formik.values.taskId
                    ? {
                        value: formik.values.taskId,
                        label:
                          tasks.find((t) => t.taskId === formik.values.taskId)
                            ?.name || '',
                      }
                    : null
                }
                onChange={(selectedOption) => {
                  const option = selectedOption as {
                    value: string;
                    label: string;
                  } | null;
                  formik.setFieldValue('taskId', option?.value || '');
                }}
                placeholder="Select a task..."
                isLoading={loadingTasks}
                isDisabled={!formik.values.projectId}
                className="w-full"
                error={formik.touched.taskId && Boolean(formik.errors.taskId)}
              />
              {formik.touched.taskId && formik.errors.taskId && (
                <p className="text-red-500 text-sm">{formik.errors.taskId}</p>
              )}
            </div>

            {/* Active Budget Warning */}
            {formik.values.taskId &&
              tasksWithActiveBudgets.has(formik.values.taskId) && (
                <Card className="bg-red-50 border-red-200 p-4">
                  <h4 className="font-semibold text-red-600 mb-2">
                    ⚠️ Active Budget Request Exists
                  </h4>
                  <p className="text-red-800 text-sm">
                    This task already has an active budget request. Creating
                    another budget request may cause conflicts. Please review
                    the existing budget request before proceeding.
                  </p>
                </Card>
              )}

            {/* Selected Task Information */}
            {formik.values.taskId &&
              (() => {
                const selectedTask = tasks.find(
                  (t) => t.taskId === formik.values.taskId,
                );
                if (!selectedTask) return null;

                return (
                  <Card className="bg-amber-50 border-amber-200 p-4">
                    <h4 className="font-semibold text-amber-800 mb-3">
                      Selected Task Details
                    </h4>
                    <div className="space-y-2">
                      <h5 className="font-medium text-gray-900">
                        {selectedTask.name}
                      </h5>

                      {selectedTask.description && (
                        <p className="text-gray-600 text-sm line-clamp-3 whitespace-pre-line">
                          {htmlToPlainText(selectedTask.description)}
                        </p>
                      )}

                      <div className="flex flex-wrap gap-4 mt-2">
                        {selectedTask.startTime && selectedTask.endTime && (
                          <div className="flex items-center gap-1 text-sm text-gray-600">
                            <CalendarMonth className="w-4 h-4 text-amber-500" />
                            <span>
                              Task Dates:{' '}
                              {new Date(
                                selectedTask.startTime,
                              ).toLocaleDateString()}{' '}
                              -{' '}
                              {new Date(
                                selectedTask.endTime,
                              ).toLocaleDateString()}
                            </span>
                          </div>
                        )}

                        {selectedTask.budget && (
                          <div className="flex items-center gap-1 text-sm text-gray-600">
                            <AttachMoney className="w-4 h-4 text-amber-500" />
                            <span>
                              Task Budget: $
                              {selectedTask.budget.toLocaleString()}
                            </span>
                          </div>
                        )}

                        {selectedTask.manHours && (
                          <div className="text-sm text-gray-600">
                            Est. Hours: {selectedTask.manHours}
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })()}

            {/* Form Fields - Hidden when active budget request exists */}
            {!(
              formik.values.taskId &&
              tasksWithActiveBudgets.has(formik.values.taskId)
            ) && (
              <>
                {/* Date Range */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                      Start Date
                    </label>
                    <div className="relative">
                      <CalendarMonth className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-amber-500" />
                      <Input
                        type="date"
                        name="requestedStartDate"
                        value={formik.values.requestedStartDate}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className="pl-10 w-full"
                        error={
                          formik.touched.requestedStartDate &&
                          Boolean(formik.errors.requestedStartDate)
                        }
                      />
                    </div>
                    {formik.touched.requestedStartDate &&
                      formik.errors.requestedStartDate && (
                        <p className="text-red-500 text-sm">
                          {formik.errors.requestedStartDate}
                        </p>
                      )}
                  </div>
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                      End Date
                    </label>
                    <div className="relative">
                      <CalendarMonth className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-amber-500" />
                      <Input
                        type="date"
                        name="requestedEndDate"
                        value={formik.values.requestedEndDate}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className="pl-10 w-full"
                        error={
                          formik.touched.requestedEndDate &&
                          Boolean(formik.errors.requestedEndDate)
                        }
                      />
                    </div>
                    {formik.touched.requestedEndDate &&
                      formik.errors.requestedEndDate && (
                        <p className="text-red-500 text-sm">
                          {formik.errors.requestedEndDate}
                        </p>
                      )}
                  </div>
                </div>

                {/* Budget Amount */}
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    Maximum Bid Amount (Optional)
                  </label>
                  <div className="relative">
                    <AttachMoney className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-amber-500" />
                    <Input
                      type="number"
                      name="maxBudgetAmount"
                      value={formik.values.maxBudgetAmount}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder="Enter amount..."
                      className="pl-10 w-full"
                      error={
                        formik.touched.maxBudgetAmount &&
                        Boolean(formik.errors.maxBudgetAmount)
                      }
                    />
                  </div>
                  {formik.touched.maxBudgetAmount &&
                    formik.errors.maxBudgetAmount && (
                      <p className="text-red-500 text-sm">
                        {formik.errors.maxBudgetAmount}
                      </p>
                    )}
                </div>

                {/* Recipient Organizations */}
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    Select Recipient Organizations
                  </label>
                  <ReactSelect
                    isMulti
                    options={organizations.map((org) => ({
                      value: org.organizationId || '',
                      label: org.name || '',
                    }))}
                    value={organizations
                      .filter((org) =>
                        formik.values.recipientOrganizationIds.includes(
                          org.organizationId,
                        ),
                      )
                      .map((org) => ({
                        value: org.organizationId || '',
                        label: org.name || '',
                      }))}
                    onChange={(selectedOptions) => {
                      const options = selectedOptions as
                        | { value: string; label: string }[]
                        | null;
                      formik.setFieldValue(
                        'recipientOrganizationIds',
                        options ? options.map((opt) => opt.value) : [],
                      );
                    }}
                    placeholder="Select organizations..."
                    isLoading={loadingOrganizations}
                    className="w-full"
                    error={
                      formik.touched.recipientOrganizationIds &&
                      Boolean(formik.errors.recipientOrganizationIds)
                    }
                  />
                  {formik.touched.recipientOrganizationIds &&
                    formik.errors.recipientOrganizationIds && (
                      <p className="text-red-500 text-sm">
                        {formik.errors.recipientOrganizationIds}
                      </p>
                    )}
                </div>

                {/* Description */}
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    Description (Optional)
                  </label>
                  <div className="relative">
                    <Description className="absolute left-3 top-3 w-5 h-5 text-amber-500" />
                    <textarea
                      name="description"
                      value={formik.values.description}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      rows={4}
                      placeholder="Enter description..."
                      className={`pl-10 w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none ${
                        formik.touched.description && formik.errors.description
                          ? 'border-red-500'
                          : 'border-gray-300'
                      }`}
                    />
                  </div>
                  {formik.touched.description && formik.errors.description && (
                    <p className="text-red-500 text-sm">
                      {formik.errors.description}
                    </p>
                  )}
                </div>

                {/* File Upload Section */}
                <div ref={documentsListRef}>
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-3">
                    Attach Documents (Optional)
                  </h4>
                  <FileDropZone
                    files={selectedFiles}
                    onFilesChange={setSelectedFiles}
                    disabled={isUploading}
                    isUploading={isUploading}
                    activeColor="indigo"
                  />
                </div>
              </>
            )}
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default CreateBudgetModal;
